import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Star, MessageCircle, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";

type ContextType = { nursingHome: NursingHome };

export default function ReviewsPage() {
  const { nursingHome } = useOutletContext<ContextType>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ["owner-reviews", nursingHome.nh_slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("*, review_responses(*)")
        .eq("nursing_home_id", nursingHome.nh_slug)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const avgRating = reviews.length > 0
    ? reviews.reduce((sum: number, r: any) => sum + r.rating, 0) / reviews.length
    : 0;

  const unansweredCount = reviews.filter(
    (r: any) => !r.review_responses || r.review_responses.length === 0
  ).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Star className="h-6 w-6" />
          Reviews
        </h1>
        <p className="text-muted-foreground">
          Manage reviews for {nursingHome.center_name}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{reviews.length}</div>
            <p className="text-xs text-muted-foreground">Total Reviews</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold flex items-center justify-center gap-1">
              {avgRating.toFixed(1)}
              <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
            </div>
            <p className="text-xs text-muted-foreground">Average Rating</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{unansweredCount}</div>
            <p className="text-xs text-muted-foreground">Awaiting Response</p>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        </div>
      ) : reviews.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Star className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-1">No reviews yet</h3>
            <p className="text-muted-foreground">
              When families leave reviews on your profile, they'll appear here for you to respond.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((review: any) => (
            <OwnerReviewCard
              key={review.id}
              review={review}
              userId={user?.id}
              nhSlug={nursingHome.nh_slug}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OwnerReviewCard({
  review,
  userId,
  nhSlug,
}: {
  review: any;
  userId?: string;
  nhSlug: string;
}) {
  const [responseText, setResponseText] = useState("");
  const [showReply, setShowReply] = useState(false);
  const queryClient = useQueryClient();
  const response = review.review_responses?.[0];

  const submitResponse = useMutation({
    mutationFn: async () => {
      if (!userId || !responseText.trim()) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("id", userId)
        .single();

      const responderName = profile
        ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
        : "Management";

      const { error } = await supabase.from("review_responses").insert({
        review_id: review.id,
        responder_user_id: userId,
        responder_name: responderName,
        body: responseText.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owner-reviews", nhSlug] });
      setResponseText("");
      setShowReply(false);
      toast.success("Response published!");
    },
    onError: () => {
      toast.error("Failed to submit response");
    },
  });

  const relationshipLabels: Record<string, string> = {
    family_member: "Family Member",
    resident: "Resident",
    visitor: "Visitor",
    professional: "Healthcare Professional",
  };

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    className={`h-4 w-4 ${
                      s <= review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                    }`}
                  />
                ))}
              </div>
              {review.title && (
                <span className="font-medium text-sm">{review.title}</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {review.reviewer_name}
              {review.relationship && ` · ${relationshipLabels[review.relationship] || review.relationship}`}
              {" · "}
              {format(new Date(review.created_at), "MMM d, yyyy")}
            </p>
          </div>
          {!response && (
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
              Needs response
            </Badge>
          )}
        </div>

        <p className="text-sm mt-3 whitespace-pre-line">{review.body}</p>

        {/* Existing response */}
        {response && (
          <div className="mt-3 ml-4 pl-3 border-l-2 border-grandpal-green/30 bg-grandpal-beige/10 rounded-r p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <MessageCircle className="h-3.5 w-3.5 text-grandpal-green" />
              <span className="text-xs font-medium text-grandpal-green-dark">
                Your response · {format(new Date(response.created_at), "MMM d, yyyy")}
              </span>
            </div>
            <p className="text-sm whitespace-pre-line">{response.body}</p>
          </div>
        )}

        {/* Reply form */}
        {!response && !showReply && (
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => setShowReply(true)}
          >
            <MessageCircle className="h-4 w-4 mr-1" />
            Write Response
          </Button>
        )}

        {showReply && (
          <div className="mt-3 flex gap-2">
            <Textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Write your public response to this review..."
              rows={3}
              className="flex-1"
            />
            <div className="flex flex-col gap-1">
              <Button
                size="sm"
                onClick={() => submitResponse.mutate()}
                disabled={!responseText.trim() || submitResponse.isPending}
              >
                <Send className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowReply(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
